import { Form, Input, Select, Tooltip } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import DashboardStore from "../stores/DashboardStore";
import WidgetConstructorStore from "src/modules/administration/stores/WidgetConstructorStore";
import { DEFAULT_DASHBOARD_VIEW_KEY } from "../models/WidgetTypes";
import { InfoCircleOutlined } from "@ant-design/icons";
import AppModal from "../../common/components/AppModal";

type Props = {
    store: DashboardStore;
    widgetStore: WidgetConstructorStore;
    viewName: string;
    closeDialog: () => void;
};

const UpdateViewDialog: React.FC<Props> = ({
    closeDialog,
    store,
    widgetStore,
    viewName,
}) => {
    const [form] = Form.useForm();
    const handleSubmit = () => {
        form.validateFields().then(async (vals) => {
            const trimmedName = vals.name?.trim();
            if (
                store.views.map((v) => v.name).includes(trimmedName) &&
                trimmedName !== viewName
            ) {
                form.setFields([
                    {
                        name: "name",
                        errors: ["View with this name already exists"],
                    },
                ]);
                return;
            }
            if (!viewName) {
                store.createView(trimmedName, vals.widgets);
            } else {
                store.updateView(viewName, trimmedName, vals.widgets);
            }
            handleClose();
        });
    };

    const handleClose = () => {
        form.resetFields();
        closeDialog();
    };

    const getWidgetOptions = () => {
        const options = [
            {
                label: "Regular widgets",
                options: widgetStore.widgets
                    .map((w) => ({ label: w.name, value: w.id }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
            },

            {
                label: "Grouped widgets",
                options: widgetStore.groupedWidgets.map((w) => ({
                    label: w.name,
                    value: w.id,
                })),
            },
        ];
        return options;
    };

    const getSelectedWidgets = () => {
        const view = store.views.find((v) => v.name === viewName);
        return view?.widgetIds ?? [];
    };

    return (
        <AppModal
            title={viewName ? "Edit view" : "New view"}
            open
            className="alpha-modal comment-in-modal"
            closable={false}
            width={450}
            cancelButtonTitle="Cancel"
            onCancelClick={handleClose}
            cancelButtonProps={{
                "data-id": "query-cancel-button",
                size: undefined,
            }}
            submitButtonTitle={viewName ? "Save changes" : "Add view"}
            submitButtonProps={{
                "data-id": "query-save-button",
                form: "query-modal-form",
                size: undefined,
            }}
        >
            <Form
                form={form}
                onFinish={handleSubmit}
                className="alpha-form"
                layout="vertical"
                id="query-modal-form"
            >
                <Form.Item
                    colon={false}
                    className="dialog-field"
                    label={<span className="dialog-field-label">Title</span>}
                    name="name"
                    rules={[{ required: true, message: "Title is required" }]}
                    initialValue={viewName}
                    getValueProps={(value) => ({
                        value:
                            value === DEFAULT_DASHBOARD_VIEW_KEY
                                ? "Default view"
                                : value,
                    })}
                >
                    <Input disabled={viewName === DEFAULT_DASHBOARD_VIEW_KEY} />
                </Form.Item>
                <Form.Item
                    colon={false}
                    className="dialog-field"
                    label={
                        <span className="dialog-field-label">
                            Widgets
                            <Tooltip title="Adding or removing widgets may change their order">
                                <InfoCircleOutlined style={{ marginLeft: 4 }} />
                            </Tooltip>
                        </span>
                    }
                    name="widgets"
                    initialValue={getSelectedWidgets()}
                >
                    <Select
                        mode="multiple"
                        options={getWidgetOptions()}
                        optionFilterProp="label"
                        showSearch
                    />
                </Form.Item>
            </Form>
        </AppModal>
    );
};
export default observer(UpdateViewDialog);
